import React, {FC, useState} from "react";
import {StackPanel} from "../panels/StackPanel";
import {QuickSearchText} from "../common/QuickFilterOption";
import {useAppSelector} from "../../hooks/ReduxHooks";
import {PgpKeysLocation} from "../../locations/PgpKeysLocation";
import {EnrichedVerificationResult} from "../../domain/pgp/EnrichedPgpKey";
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {PgpKeyListControlsView} from "./PgpKeyListControlsView";
import {getKeyFirstUserId, getKeyHexId, getKeyUserIds, PgpKey} from "../../domain/PgpKey";
import {shallowEqual} from "react-redux";
import {KeyID} from "openpgp";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GridOptions, SelectionChangedEvent} from "ag-grid-community";
import {DefaultGridOptions, DefaultSingleRowSelection, gridTheme, gridThemeDark} from "../../util/AgGridUtil";

interface PgpKeyGridRowProps {
    pgpKey?: PgpKey;
    keyId?: KeyID;
    verification?: EnrichedVerificationResult;
}

export const PgpKeyGridRow: FC<PgpKeyGridRowProps> = ({pgpKey, keyId, verification}) => {

    const isMobile = useAppSelector(s => s.system.isMobile);

    const icon = verification &&
        (<span className={verification.valid ? "success" : "error"}>{verification.valid ? <CheckIcon/> : <ErrorOutlineIcon/>}</span>);

    return (<>
        {icon && <div className="grid-icon">{icon}</div>}

        <div className="grid-user">{getKeyFirstUserId(pgpKey) || keyId?.toHex()}</div>

        {!isMobile && <div className="grid-key-id">{keyId?.toHex() || getKeyHexId(pgpKey?.key)}</div>}

        <div className="grid-expiry">{pgpKey ? pgpKey.enrichedKey?.expiryDate : "Unknown Key"}</div>
    </>);
};

export const PgpKeyListView = () => {
    const [filterText, setFilterText] = useState<string>("");

    const isDark = useAppSelector(s => s.system.theme === "dark");

    // TODO: Selector unknown returned a different result when called with the same parameters. This can lead to unnecessary rerenders.
    const allPublicKeys = useAppSelector(s => s.accountsState.pgpKeys, shallowEqual);
    const allSecretKeys = useAppSelector(s => s.accountsState.secretKeys, shallowEqual);
    const isMobile = useAppSelector(s => s.system.isMobile);

    const allKeys = [...allSecretKeys, ...allPublicKeys.filter(pk => !allSecretKeys.some(sk => sk.keyHexId === pk.keyHexId))];

    const lowerFilterText = filterText.toLowerCase();

    const filteredKeys = (filterText.length === 0
        ? [...allKeys]
        : allKeys.filter(k => getKeyUserIds(k).some(u => u.toLowerCase().includes(lowerFilterText))))
        .sort((a, b) => getKeyUserIds(a)[0].localeCompare(getKeyUserIds(b)[0]));

    const gridOptions: GridOptions<PgpKey> = {
        ...DefaultGridOptions,
        rowClassRules: {
            "error": params => !!params.data?.enrichedKey?.verificationError,
            "secret-key-row": params => !!params.data?.isPrivate,
        },
    };

    const columnDefs: ColDef<PgpKey>[] = [
        {
            headerName: "User",
            flex: 1,
            sortable: true,
            sort: "asc",
            valueGetter: params => params.data?.enrichedKey?.primaryUserString || params.data?.keyHexId || "",
        },
        {
            headerName: "Key Id",
            hide: isMobile,
            width: 140,
            sortable: true,
            valueGetter: params => params.data?.keyHexId,
        },
        {
            field: "enrichedKey.expiryDate",
            width: 80,
            sortable: true,
            headerName: "Expiry",
        },
    ];

    function handleSelectionChanged(params: SelectionChangedEvent<PgpKey>) {
        const newSelectedKeys = params.api.getSelectedRows();
        PgpKeysLocation.selectPgpKey(getKeyHexId(newSelectedKeys[0]?.key));
    }

    return (
        <>
            <StackPanel orientation="horizontal" className="panel top-panel" align={"middle"} fill={true}>
                <QuickSearchText onQuickSearchTextChange={text => setFilterText(text)}
                                 searchPrompt={"Filter Keys"}/>
                <PgpKeyListControlsView/>
            </StackPanel>

            <div className="pgp-grid">
                <AgGridReact<PgpKey> theme={isDark ? gridThemeDark : gridTheme}
                                     gridOptions={gridOptions}
                                     rowSelection={DefaultSingleRowSelection}
                                     rowData={filteredKeys}
                                     columnDefs={columnDefs}
                                     getRowId={d => d.data?.keyHexId}
                                     onSelectionChanged={handleSelectionChanged}
                />
            </div>
        </>
    );
}
